import React from "react"
import { useNavigate } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import { IoMdCheckmark } from "react-icons/io"
import { BsTrash3Fill } from "react-icons/bs"
import { useDispatch } from "react-redux"
import {
	updateSchedule,
	setIsEditing,
	setOptimizeData,
	transferPatientsBetweenNursesAndChairs,
	updatePatientInSchedule,
	addPatientField
} from "../../features/schedule/scheduleSlice"
import axios from "axios"
import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import store from "../../store"
import { getDateFormatted, getDateToday } from "../../utils/generalUtils"
import { getScheduleFromMongo } from "../../utils/mongoUtils"
import { useSchedulePatch } from "../../hooks/useSchedulePatch"
import useLogging from "../../hooks/useLogging"
import { usePostHog } from "posthog-js/react"

function ActionableNotificationItem({ notification }) {
	const { getAccessTokenSilently } = useAuth0()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const { user } = useAuth0()
	const posthog = usePostHog()

	const logging = useLogging()
	const schedulePatch = useSchedulePatch({ isTomorrowMode: false, sandbox: false })

	// Determine if we are in the test environment
	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const date = getDateFormatted(getDateToday(testEnvironment))

	const currentState = store.getState()["schedule"]

	function getPatientName(patientId) {
		const patient = currentState.patientSchedule.find(
			patient => patient.patientId === patientId
		)
		return patient ? patient.patientName : "Patient not found"
	}

	const handleClick = () => {
		if (notification.description.toLowerCase().includes("settings")) {
			navigate("/settings")
		} else {
			navigate("/ic-tracker")
		}
	}

	const fetchNotifications = async () => {
		try {
			await queryClient.invalidateQueries({ queryKey: ["notifications"] })
		} catch (error) {
			logging.error("Error refreshing notifications:", {
				error: error.message,
				stack: error.stack
			})
		}
	}

	const handleApproveForEdit = async () => {
		try {
			// Get the proposed changes out of the notification payload
			const updatedPatient = notification.additionalData.proposedChanges
			const { patientId, assignedNurse, assignedChair } = updatedPatient

			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})
				await getScheduleFromMongo(
					token,
					date,
					dispatch,
					currentState,
					false,
					false,
					null,
					setOptimizeData,
					logging
				)
			} catch (error) {
				logging.error("Error fetching schedule from mongo:", error)
			}

			// Transfer the patient between nurses and chairs
			dispatch(
				transferPatientsBetweenNursesAndChairs([
					{
						newNurseId: assignedNurse,
						newChairId: assignedChair,
						patientId: patientId
					}
				])
			)

			if (updatedPatient.acuity) {
				dispatch(updatePatientInSchedule({ updatedPatient }))
				dispatch(addPatientField({ patientId, fieldName: "acuity" }))
			}

			try {
				await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})
				await schedulePatch.mutateAsync({ date })
			} catch (error) {
				logging.error("Error updating schedule in mongo:", {
					error: error.message,
					stack: error.stack
				})
			}

			// Send the approval notification
			const approvalNotification = {
				notification_name: `Approval Notice: Changes approved for ${updatedPatient.patientName}`,
				description: `The requested changes for patient ${updatedPatient.patientName} have been approved!`,
				person_id: notification.additionalData.requestedBy.sub,
				importance: "high",
				time: new Date().toISOString(),
				icon: "✅",
				active: true
			}

			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
				approvalNotification,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			)

			// Update the decision on the original notification
			const approvalNotificationUpdate = {
				decision: true,
				decisionValue: "Approved"
			}
			await axios.put(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notification._id}`,
				approvalNotificationUpdate,
				{ headers: { Authorization: `Bearer ${token}` } }
			)

			await fetchNotifications()
		} catch (error) {
			logging.error("Error handling approval:", {
				error: error.message,
				stack: error.stack
			})
			throw error
		} finally {
			dispatch(setIsEditing(false))
		}
	}

	const handleReject = async () => {
		posthog.capture("reject_notification", {
			notification_name: notification.notification_name,
			description: notification.description,
			requested_by: notification.additionalData.requestedBy.sub,
			patient_id: notification.additionalData.patientId
		})
		try {
			const rejectNotificationData = {
				notification_name: `Request Rejected: Changes rejected for ${getPatientName(
					notification.additionalData.patientId
				)}`,
				description: `The request for changes made for patient ${getPatientName(
					notification.additionalData.patientId
				)} has been rejected.`,
				person_id: notification.additionalData.requestedBy.sub,
				importance: "high",
				time: new Date().toISOString(),
				icon: "🚫",
				active: true
			}

			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
				rejectNotificationData,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			)

			const rejectionNotificationUpdate = {
				decision: true,
				decisionValue: "Rejected"
			}
			await axios.put(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notification._id}`,
				rejectionNotificationUpdate,
				{ headers: { Authorization: `Bearer ${token}` } }
			)

			const updatedPatientSchedule = currentState.patientSchedule.map(patient =>
				patient.patientId === notification.additionalData.patientId
					? { ...patient, pendingChanges: false, notes: "" }
					: patient
			)

			dispatch(updateSchedule({ patientSchedule: updatedPatientSchedule }))

			await schedulePatch.mutateAsync({ date })

			await fetchNotifications()
		} catch (error) {
			logging.error("Error sending rejection notification:", {
				error: error.message,
				stack: error.stack
			})
			throw error
		}
	}

	return (
		<div
			className={`relative bg-customBg shadow-sm rounded-md p-4 mb-4 flex items-center justify-between cursor-pointer ${
				notification.active ? "font-bold" : ""
			}`}
			onClick={handleClick}
		>
			<div className="flex items-center">
				<div className="mr-4">
					<span
						aria-label="icon"
						className="relative inline-flex items-center justify-center w-12 h-12 shadow-md rounded-full bg-white"
					>
						<span className="text-2xl">{notification.icon}</span>
						{notification.active && (
							<span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full"></span>
						)}
					</span>
				</div>
				<div className="prose prose-slate prose-sm text-black prose-li:text-black sensitive ph-no-capture">
					<div className="font-bold mb-2 sensitive">{notification.notification_name}</div>
					<ReactMarkdown>{notification.description}</ReactMarkdown>
					<div className="text-gray-400 text-sm">{notification.time}</div>
				</div>
			</div>
			{!notification.decision || notification.decision === false ? (
				<div className="absolute bottom-4 right-4 space-x-2 flex">
					<button
						className="py-2 w-28 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
						onClick={handleReject}
					>
						<BsTrash3Fill className="h-4 w-4" />
						Reject
					</button>
					<button
						className="py-2 w-28 bg-green-50 border border-green-200 text-green-600 rounded-lg hover:bg-green-100 flex items-center justify-center gap-2"
						onClick={() => {
							// Uncomment the next line if you need to handle delete requests separately:
							// isDeleteRequest ? handleApproveForDelete() : handleApproveForEdit();
							dispatch(setIsEditing(true))
							handleApproveForEdit()
						}}
					>
						<IoMdCheckmark className="h-4 w-4" />
						Approve
					</button>
				</div>
			) : (
				<div className="absolute bottom-4 right-4 text-sm font-bold">
					{notification.decisionValue === "Approved" ? (
						<span className="text-green-600">Approved</span>
					) : (
						<span className="text-red-600">Rejected</span>
					)}
				</div>
			)}
		</div>
	)
}

export default ActionableNotificationItem
